import React from 'react';
import { Typography, Divider } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { formatToLocalDateTime } from '../../../utils/dateFormatter';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import OutgoingMailIcon from '@mui/icons-material/OutgoingMail';
import { sendInvoiceEmail } from '../../../api/invoiceService';
import { useSnackbar } from '../../../context/SnackbarContext';
import ConfirmDialog from '../../common/modals/ConfirmDialog';
import { BottomNavigation, BottomNavigationAction, Box } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import PaidIcon from '@mui/icons-material/Paid';
import InvoiceItemsList from '../lists/InvoiceItemsList';
import { numberToCurrencyFormat } from '../../../utils/numberToCurrencyFormat';
import InvoicePaymentsList from '../lists/InvoicePaymentsList';
import InvoicePreview from './InvoicePreview';
import PaymentMethodsSeederDialog from './PaymentMethodsSeederDialog';
import { usePaymentMethods } from '../../../hooks/appointments/usePaymentMethods';

export default function InvoiceView({handleEdit, open, onClose, invoice = null, successCallback, onInvoiceItemSaveSuccess, onInvoiceItemDeleteSuccess, onInvoicePaymentSaveSuccess}) { 
    const { t } = useTranslation('common'); 
    const { showSnackbar } = useSnackbar();
    const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

    const [previewOpen, setPreviewOpen] = React.useState(false);

    const [tabIndex, setTabIndex] = React.useState(0);

    const [seederDialogOpen, setSeederDialogOpen] =  React.useState(false);
      
    const onEmptyData = () => {
        setSeederDialogOpen(true);
    };
          
    const { paymentMethods, loading, setPaymentMethods } = usePaymentMethods(null, onEmptyData);

    const handleClose = () => {
        onClose();
    };

    const handlePreviewClick = () => {
      setPreviewOpen(true);
    };

    const handleSendEmailClick = async () => {
      try {
        setConfirmDialogOpen(false);
        await sendInvoiceEmail(invoice.id);
        showSnackbar(t('invoice.messages.send_email_success'), "success");
      } catch (error) {
        showSnackbar(t('invoice.messages.send_email_fail'), "error");
      }
    };

    const handleEditClick = () => {
        handleEdit(invoice);
    };

    if (!invoice) {
        return (
            <></>
        );
    }

    return (
        <React.Fragment>
        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            
            <DialogTitle>
              <>
              <Typography variant="body2" sx={{ mt: 0 }}>{`Invoice ${invoice.invoice_number}`}</Typography>
              <Typography variant="h6" component="span" sx={{ mb: 0 }}>{`${invoice.pet.name} (${invoice.pet.owner.last_name}, ${invoice.pet.owner.first_name})`}</Typography>
              <Typography variant="body1" sx={{ mt: 0 }}>{invoice.date}</Typography>
              </>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                minHeight: 500,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                {tabIndex === 0 ? (
                  <>
                    <Typography variant="h6" >Details</Typography>
                    <br />
                    <Box sx={{
                      maxHeight: 400,
                      overflow: "auto"
                    }}>
                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Invoice Number</Typography>
                      <Typography variant="body1" sx={{ mt: 0 }}>{invoice.invoice_number}</Typography>

                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Patient</Typography>
                      <Typography variant="body1" sx={{ mt: 0 }}>
                        {`${invoice.pet.name} (${invoice.pet.owner.last_name}, ${invoice.pet.owner.first_name})`}
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Date</Typography>
                      <Typography variant="body1" sx={{ mt: 0 }}>{invoice.date}</Typography>

                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Due Date</Typography>
                      <Typography variant="body1" sx={{ mt: 0 }}>{invoice.due_date || "(none)"}</Typography>

                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Total Amount</Typography>
                      <Typography variant="body1" sx={{ mt: 0 }}>{numberToCurrencyFormat(invoice.calculated_subtotal)}</Typography>

                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Amount Paid</Typography>
                      <Typography variant="body1" sx={{ mt: 0 }}>{numberToCurrencyFormat(invoice.calculated_total_paid)}</Typography>

                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Remaining Balance</Typography>
                      <Typography variant="body1" sx={{ mt: 0 }}>{numberToCurrencyFormat(invoice.calculated_balance_remaining)}</Typography>

                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Status</Typography>
                      <Typography variant="body1" sx={{ mt: 0 }}>{invoice.status}</Typography>

                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Remarks</Typography>
                      <Typography variant="body1" sx={{ mt: 0, whiteSpace: 'pre-line' }}>{invoice.remarks || "(none)"}</Typography>

                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Created</Typography>
                      <Typography variant="body2" sx={{ mt: 0 }}>{formatToLocalDateTime(invoice.createdAt)}</Typography>

                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Last Updated</Typography>
                      <Typography variant="body2" sx={{ mt: 0 }}>{formatToLocalDateTime(invoice.updatedAt)}</Typography>

                      <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Created By</Typography>
                      <Typography variant="body2" sx={{ mt: 0 }}>{invoice.user.name}</Typography>
                    </Box>
                  </>
                ) : null}

                {tabIndex === 1 ? (
                  <>
                    <Typography variant="h6" >Items</Typography>
                    <br />
                    <Box sx={{
                      maxHeight: 400,
                      overflow: "auto"
                    }}>
                      <InvoiceItemsList 
                        invoice={invoice}
                        onInvoiceItemSaveSuccess={onInvoiceItemSaveSuccess}
                        onInvoiceItemDeleteSuccess={onInvoiceItemDeleteSuccess}
                      />
                    </Box>
                  </>
                ) : null}

                {tabIndex === 2 ? (
                  <>
                    <Typography variant="h6" >Payments</Typography>
                    <br />
                    <Box sx={{
                      maxHeight: 400,
                      overflow: "auto"
                    }}>
                      <InvoicePaymentsList 
                        invoice={invoice}
                        onSaveSuccess={onInvoicePaymentSaveSuccess}
                        onDeleteSuccess={onInvoiceItemDeleteSuccess}
                      />
                    </Box>
                    
                  </>
                ) : null}
              </Box>

              <BottomNavigation
                showLabels
                value={tabIndex}
                onChange={(event, newIndex) => {
                  setTabIndex(newIndex);
                }}
              >
                <BottomNavigationAction label="Details" icon={<InfoIcon />} />
                <BottomNavigationAction label="Items" icon={<FormatListNumberedIcon />} />
                <BottomNavigationAction label="Payments" icon={<PaidIcon />} />
              </BottomNavigation>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleEditClick}>
                  <EditIcon />
              </Button>
              <Button onClick={handlePreviewClick}>
                  <FindInPageIcon />
              </Button>
              <Button onClick={() => setConfirmDialogOpen(true)}>
                  <OutgoingMailIcon />
              </Button>
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <InvoicePreview 
          invoice={invoice}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
        <ConfirmDialog
          open={confirmDialogOpen}
          title="Send Invoice E-mail"
          content={t('invoice.messages.send_email_confirm')}
          confirmText="Send E-mail"
          cancelText="Cancel"
          onConfirm={handleSendEmailClick}
          onCancel={() => setConfirmDialogOpen(false)}
        />
        <PaymentMethodsSeederDialog
          open={seederDialogOpen}
          setOpen={setSeederDialogOpen} 
        />
        </React.Fragment>
    );
}